import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  Cartao,
  Selo,
  useDialogo,
} from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import { useExercicioVigente } from '../../servicos/exercicio';
import {
  CHAVE_DO_SISCRED,
  carregarSiscred,
  encerrarSiscred,
  reabrirSiscred,
} from '../../servicos/siscred';
import { ErroDaApi } from '../ErroDaApi';

interface ResultadoDoAviso {
  readonly ano: number;
  readonly tipo: string;
  readonly clientes: number;
  readonly fornecedores: number;
  readonly assessores: number;
  /** Contratos do ano que a regra deixou de fora (RN-076). */
  readonly contratosIgnorados: number;
  readonly enviado: boolean;
  /**
   * Endereços alcançados. **Zero na prévia**, e não porque ninguém receberia:
   * resolver os contatos de cada empresa é uma consulta por empresa, e a prévia
   * não paga esse custo. O número real só existe depois do envio.
   */
  readonly destinatarios: number;
  /** `true` quando as mensagens foram montadas e não saíram (modo ensaio). */
  readonly ensaio?: boolean;
}

/**
 * RN-114, RN-076 — Aviso de encerramento do SISCRED, em massa.
 *
 * ## O que este envio é
 *
 * O SISCRED tem prazo. Quando ele se aproxima — e depois, quando encerra — todo
 * contribuinte com crédito no ano precisa ser avisado. Não é um e-mail do
 * cronograma: não tem competência, não tem agendamento, e sai para a carteira
 * inteira de uma vez.
 *
 * ## Prévia antes de enviar, sempre
 *
 * A rota tem `confirmar: false` por padrão e devolve só a contagem de quem
 * receberia. A tela usa isso como primeiro passo obrigatório: um envio em massa
 * que sai no primeiro clique é um envio em massa que sai errado uma vez por ano,
 * e não dá para voltar atrás.
 *
 * **Dívida técnica #5** — no legado esta rota não exigia admin. Qualquer conta
 * autenticada disparava e-mail para a base inteira.
 *
 * ## Onde vive
 *
 * É uma aba de Configurações da divisão Créditos ICMS Recorrente, não uma tela
 * com item de menu próprio: é um disparo que acontece uma ou duas vezes por
 * ano, e um item de menu permanente para isso é um convite a clicar sem querer.
 * O componente não tem `Pagina` em volta por isso — o título é da página de
 * configurações.
 *
 * ## O exercício vem preenchido com o ano corrente
 *
 * O aviso é sempre sobre o SISCRED do ano em curso; o campo continua editável
 * para o caso raro de disparo atrasado (janeiro avisando sobre o ano anterior).
 */
export function AvisoSiscred(): JSX.Element {
  const [tipo, setTipo] = useState<'AVISO_ENCERRAMENTO_SISCRED' | 'SISCRED_ENCERRADO'>(
    'AVISO_ENCERRAMENTO_SISCRED',
  );
  // RN-620 — o exercício vigente: a partir de julho, o ano seguinte.
  const vigente = useExercicioVigente();
  const [ano, setAno] = useState(String(vigente));
  const [previa, setPrevia] = useState<ResultadoDoAviso | null>(null);
  const { confirmar } = useDialogo();

  // O parâmetro chama-se `confirmado` (e não `confirmar`, como o campo do corpo)
  // para não se confundir com o `confirmar` do diálogo, que vive no mesmo escopo.
  const executar = useMutation({
    mutationFn: (confirmado: boolean) =>
      requisicaoAutenticada<ResultadoDoAviso>('/icms/send-email', {
        metodo: 'POST',
        corpo: { tipo, ano: Number(ano), confirmar: confirmado },
      }),
    onSuccess: (dados, confirmado) => {
      if (!confirmado) setPrevia(dados);
    },
  });

  // Segunda confirmação, já dentro do cartão que explica a consequência: um
  // envio para a carteira inteira merece o atrito de mais um clique.
  async function enviarAgora(total: number): Promise<void> {
    const ok = await confirmar({
      titulo: `Enviar para os contatos de ${total} empresa(s)?`,
      descricao:
        'O e-mail sai imediatamente para todos os contatos dessas empresas.\n\nNão dá para desfazer.',
      rotuloConfirmar: 'Enviar',
      tom: 'perigo',
    });
    if (ok) executar.mutate(true);
  }

  const enviado = executar.isSuccess && executar.variables === true;

  return (
    <section className="flex flex-col gap-4" aria-labelledby="aviso-siscred-titulo">
      <div>
        <h2 id="aviso-siscred-titulo" className="font-display text-lg font-semibold tracking-tight">
          Aviso de SISCRED
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">
          Comunicação em massa sobre o prazo do SISCRED. Sai para todos os contribuintes com crédito
          no exercício.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <Cartao como="section" titulo="O que enviar">
          <div className="flex flex-col gap-4">
            <CampoDeSelecao
              rotulo="Tipo de aviso"
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value as typeof tipo);
                setPrevia(null);
                executar.reset();
              }}
              opcoes={[
                {
                  valor: 'AVISO_ENCERRAMENTO_SISCRED',
                  rotulo: 'Aviso de proximidade do encerramento',
                },
                { valor: 'SISCRED_ENCERRADO', rotulo: 'SISCRED encerrado' },
              ]}
              ajuda="São dois e-mails distintos, com templates distintos — no legado dividiam o mesmo id e o contribuinte recebia o mesmo texto nas duas ocasiões."
            />

            <Campo
              rotulo="Exercício"
              type="number"
              min={2000}
              max={2100}
              ajuda="Padrão: o ano corrente."
              value={ano}
              onChange={(e) => {
                setAno(e.target.value);
                setPrevia(null);
                executar.reset();
              }}
            />

            {tipo === 'SISCRED_ENCERRADO' && /^\d{4}$/.test(ano) && (
              <SituacaoDoSiscredNoAno ano={Number(ano)} />
            )}

            <Botao
              variante="secundario"
              carregando={executar.isPending && executar.variables === false}
              onClick={() => executar.mutate(false)}
              larguraTotal
            >
              Ver quem receberia
            </Botao>
          </div>
        </Cartao>

        <div className="flex flex-col gap-4">
          {previa === null ? (
            <Aviso tom="informacao" titulo="Comece pela prévia">
              Antes de enviar, veja quantos contribuintes seriam alcançados. Envio em massa não tem
              desfazer.
            </Aviso>
          ) : (
            <>
              <Cartao
                como="section"
                titulo="Alcance deste envio"
                acao={<Selo tom="atencao">{totalDeEmpresas(previa)} empresa(s)</Selo>}
              >
                <dl className="grid grid-cols-3 gap-4">
                  <Contagem rotulo="Clientes" valor={previa.clientes} />
                  <Contagem rotulo="Fornecedores" valor={previa.fornecedores} />
                  <Contagem rotulo="Assessores" valor={previa.assessores} />
                </dl>

                <p className="mt-3 text-sm text-conteudo-secundario">
                  Cada empresa pode ter mais de um contato — o número de endereços só é resolvido no
                  envio, e aparece aqui depois.
                </p>

                {previa.contratosIgnorados > 0 && (
                  <p className="mt-2 text-xs text-conteudo-sutil">
                    {previa.contratosIgnorados} contrato(s) de {ano} ficaram de fora por não estarem
                    em estado que justifique o aviso.
                  </p>
                )}

                {totalDeEmpresas(previa) === 0 && (
                  <p className="mt-2 text-sm text-conteudo-sutil">
                    Ninguém seria alcançado — confira o exercício.
                  </p>
                )}
              </Cartao>

              {!enviado && totalDeEmpresas(previa) > 0 && (
                <Cartao como="section" titulo="Confirmar envio" variante="contorno">
                  <p className="text-sm text-conteudo-secundario">
                    O e-mail sai imediatamente para os contatos de {totalDeEmpresas(previa)}{' '}
                    empresa(s). Não há agendamento nem cancelamento depois do clique.
                  </p>
                  <div className="mt-3">
                    <Botao
                      variante="perigo"
                      carregando={executar.isPending && executar.variables === true}
                      onClick={() => void enviarAgora(totalDeEmpresas(previa))}
                    >
                      Enviar agora
                    </Botao>
                  </div>
                </Cartao>
              )}
            </>
          )}

          {enviado && (
            <Aviso
              tom={executar.data.ensaio === true ? 'informacao' : 'positivo'}
              titulo={
                executar.data.ensaio === true
                  ? 'Modo ensaio — nada saiu'
                  : `Enviado para ${executar.data.destinatarios} endereço(s)`
              }
            >
              {executar.data.ensaio === true
                ? 'As mensagens foram montadas e os destinatários resolvidos, mas o envio está contido. Defina EMAIL_ENVIO_REAL=1 para liberar.'
                : 'O comunicado foi disparado.'}
            </Aviso>
          )}

          {executar.isError && <ErroDaApi titulo="Não foi possível" erro={executar.error} />}
        </div>
      </div>
    </section>
  );
}

/* ─── O SISCRED do ano: aberto ou fechado ─────────────────────────────────── */

/** Hoje, no formato do campo de data. */
function hojeIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Aberto ou fechado (27/10/2026). Fechar pede a data em que o limite global
 * foi atingido; ao confirmar, o ano vigente do Recorrente vira, os pedidos de
 * transferência do ano agendados dali em diante param de sair e a data vai no
 * e-mail "SISCRED encerrado". Reabrir desfaz tudo — nada é apagado.
 */
function SituacaoDoSiscredNoAno(props: { readonly ano: number }): JSX.Element {
  const { ano } = props;
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();
  const situacao = useQuery({ queryKey: CHAVE_DO_SISCRED, queryFn: carregarSiscred });
  const encerramento = situacao.data?.encerramentos.find((e) => e.ano === ano) ?? null;
  const fechado = encerramento !== null;

  /** Fechando (ou mudando a data): o campo aparece. */
  const [editando, setEditando] = useState(false);
  const [data, setData] = useState('');
  const abrirCampo = (): void => {
    setData(encerramento?.encerradoEm ?? (ano === new Date().getFullYear() ? hojeIso() : ''));
    setEditando(true);
  };

  const recarregar = (): void => {
    setEditando(false);
    void queryClient.invalidateQueries({ queryKey: CHAVE_DO_SISCRED });
  };
  const encerrar = useMutation({
    mutationFn: () => encerrarSiscred(ano, data),
    onSuccess: recarregar,
  });
  const reabrir = useMutation({ mutationFn: () => reabrirSiscred(ano), onSuccess: recarregar });

  const alternar = async (): Promise<void> => {
    if (!fechado) {
      if (editando) setEditando(false);
      else abrirCampo();
      return;
    }
    const ok = await confirmar({
      titulo: `Reabrir o SISCRED de ${ano}?`,
      descricao: `O ano vigente volta a seguir o calendário, e os pedidos de transferência e descritivos de ${ano} que estavam parados voltam para a fila do cronograma.`,
      rotuloConfirmar: 'Reabrir',
    });
    if (ok) reabrir.mutate();
  };

  const ligadoNaTela = fechado || editando;
  const dataBr = (iso: string): string => iso.split('-').reverse().join('/');

  return (
    <div className="flex flex-col gap-3 rounded-card border border-borda p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-conteudo-primario">SISCRED de {ano}</span>
          {(fechado || editando) && (
            <span className="text-xs text-conteudo-secundario">
              {fechado
                ? `Fechado em ${dataBr(encerramento.encerradoEm)}`
                : 'Informe a data do encerramento'}
            </span>
          )}
        </div>
        {/* Um interruptor simples: a bolinha vai de um lado ao outro, sem cor de sinal. */}
        <div className="flex shrink-0 items-center gap-2 text-xs">
          <span
            className={ligadoNaTela ? 'text-conteudo-sutil' : 'font-medium text-conteudo-primario'}
          >
            Aberto
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={ligadoNaTela}
            aria-label={`SISCRED de ${ano}: ${fechado ? 'fechado' : 'aberto'}`}
            disabled={situacao.isPending || reabrir.isPending}
            onClick={() => void alternar()}
            className="relative inline-block h-5 w-9 shrink-0 rounded-full border border-borda bg-superficie-base transition-colors disabled:opacity-50"
          >
            <span
              aria-hidden="true"
              className={`absolute left-0.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full bg-conteudo-secundario transition-transform ${
                ligadoNaTela ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <span
            className={ligadoNaTela ? 'font-medium text-conteudo-primario' : 'text-conteudo-sutil'}
          >
            Fechado
          </span>
        </div>
      </div>

      {editando && (
        <div className="flex flex-col gap-3">
          <Campo
            rotulo="Data em que o SISCRED fechou"
            type="date"
            min={`${ano}-01-01`}
            max={`${ano}-12-31`}
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
          <p className="text-xs text-conteudo-secundario">
            Ao confirmar: o ano vigente do Recorrente passa a ser {ano + 1}; os pedidos de
            transferência e descritivos de {ano} agendados a partir dessa data param de sair (ficam
            na fila, sem apagar); as pendências de {ano} e dos anos anteriores saem do sininho e das
            notificações; e a data vai no e-mail "SISCRED encerrado".
          </p>
          <div className="flex gap-2">
            <Botao
              tamanho="pequeno"
              carregando={encerrar.isPending}
              disabled={data === ''}
              onClick={() => encerrar.mutate()}
            >
              {fechado ? 'Salvar a data' : 'Confirmar encerramento'}
            </Botao>
            <Botao tamanho="pequeno" variante="sutil" onClick={() => setEditando(false)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}
      {fechado && !editando && (
        <button
          type="button"
          onClick={abrirCampo}
          className="self-start text-xs text-acento-ciano underline-offset-4 hover:underline"
        >
          Corrigir a data
        </button>
      )}
      {(encerrar.error ?? reabrir.error) !== null && (
        <ErroDaApi titulo="Não foi possível" erro={encerrar.error ?? reabrir.error} />
      )}
    </div>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

function totalDeEmpresas(p: ResultadoDoAviso): number {
  return p.clientes + p.fornecedores + p.assessores;
}

function Contagem(props: { readonly rotulo: string; readonly valor: number }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="valor-fiscal text-lg text-conteudo-primario">{props.valor}</dd>
    </div>
  );
}
